import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDashboardLanguage, useLanguageController } from "../../i18n/useDashboardLanguage";
import type { LocaleCode } from "../../i18n/locales";
import { cn } from "../../utils/cn";
import { createLanguageMenuSelection } from "./languageMenuSelection";
import { Icon } from "../ui/Icon";
import { Popover } from "../ui/Popover";

export const LanguageMenu = ({ placement = "topbar" }: { placement?: "topbar" | "auth" }) => {
  const { t } = useTranslation("language");
  const language = useDashboardLanguage();
  const current = language.locales.find((locale) => locale.code === language.displayedLocale);
  return (
    <Popover
      align="end"
      triggerAriaLabel={t(($) => $.trigger, { language: current?.name ?? language.displayedLocale })}
      panelAriaLabel={t(($) => $.preferencesTitle)}
      triggerClassName={placement === "auth"
        ? "h-8 w-8 shrink-0 justify-center rounded-md border border-border/50 bg-muted/70 text-muted-foreground hover:text-foreground"
        : "justify-center rounded-md border border-border/50 bg-muted/70 px-1.5 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground xl:px-3 xl:py-2"}
      disableMobileModal
      panelClassName="w-52 max-w-[calc(100vw-1.5rem)] p-2"
      trigger={<span className="flex min-w-0 items-center gap-2">
        <Icon icon="lucide:globe" className="h-4 w-4 shrink-0" aria-hidden />
        {placement === "topbar" && <span lang={current?.code} className="hidden text-xs xl:inline">{current?.name}</span>}
      </span>}
      renderContent={(close) => <CompactLanguageChoices onClose={close} />}
    />
  );
};

const flags: Record<LocaleCode, string> = {
  en: "en", de: "de", da: "dk", es: "es", fr: "fr", "pt-BR": "pt", "zh-CN": "zh-cn",
};

export const CompactLanguageChoices = ({ onClose }: { onClose: () => void }) => {
  const language = useDashboardLanguage();
  const controller = useLanguageController();
  const { t } = useTranslation("language");
  const selection = useMemo(() => createLanguageMenuSelection(controller, onClose), [controller, onClose]);
  useEffect(() => selection.mount(), [selection]);
  const saving = language.saveStatus === "saving";
  const blocked = saving || language.contextStatus !== "ready" || language.saveStatus === "uncertain";
  const confirmed = language.mode === "guest" ? language.guestLocale : language.sessionLocale;
  const displayed = language.locales.find(locale => locale.code === language.displayedLocale)?.name ?? language.displayedLocale;
  const error = language.contextStatus === "error" ? t($ => $.loadError)
    : language.saveStatus === "uncertain" ? t($ => $.uncertain)
    : language.saveStatus === "conflict" ? t($ => $.conflict)
    : language.errorCode === "unauthorized" ? t($ => $.unauthorized)
    : language.errorCode === "forbidden" ? t($ => $.forbidden)
    : language.errorCode === "invalid" ? t($ => $.invalid)
    : language.saveStatus === "error" ? t($ => $.saveError) : null;
  const status = saving ? t($ => $.saving)
    : language.contextStatus === "loading" ? t($ => $.loading)
    : language.catalogStatus === "error" ? t($ => $.catalogError, { language: displayed })
    : language.catalogStatus === "loading" && language.saveStatus === "saved" ? t($ => $.savedLoading) : null;
  const rowClass = "flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:cursor-not-allowed disabled:opacity-60";
  return (
    <div className="min-h-0 overflow-y-auto qb-scroll" aria-busy={saving}>
      <div role="group" aria-label={t($ => $.title)}>
        {language.locales.map(locale => (
          <button key={locale.code} type="button" disabled={blocked}
            aria-pressed={language.displayedLocale === locale.code}
            onClick={() => void selection.select(locale.code)}
            className={cn(rowClass, language.displayedLocale === locale.code ? "bg-muted/70 text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground")}>
            <img src={`/uploads/flags/${flags[locale.code]}.png`} alt="" aria-hidden className="h-4 w-4 shrink-0 rounded-sm" />
            <span lang={locale.code} className="min-w-0 flex-1 text-left">{locale.name}</span>
            {language.displayedLocale === locale.code && <Icon icon="lucide:check" className="h-3 w-3 shrink-0" aria-hidden />}
          </button>
        ))}
      </div>
      <div className="mt-1 border-t border-border/50 pt-1">
        <button type="button" disabled={blocked || confirmed === null} onClick={() => void selection.select(null)}
          className={cn(rowClass, "text-muted-foreground hover:bg-muted/60 hover:text-foreground text-left")}>
          {language.mode === "guest" ? t($ => $.automatic) : t($ => $.inherit)}
        </button>
      </div>
      <div aria-live="polite" aria-atomic="true" className="space-y-2 text-xs">
        {status && <p className="px-3 py-1 text-muted-foreground">{status}</p>}
        {error && <p className="px-3 py-1 text-destructive-text">{error}</p>}
        {language.contextStatus === "stale" && <p className="px-3 py-1 text-muted-foreground">{t($ => $.stale)}</p>}
        {language.mode === "guest" && !language.storageAvailable && <p className="px-3 py-1 text-muted-foreground">{t($ => $.storageWarning)}</p>}
      </div>
      {(language.contextStatus === "error" || language.contextStatus === "stale" || language.saveStatus === "uncertain") && (
        <button type="button" onClick={() => void language.retryContext()} className={cn(rowClass, "text-left hover:bg-muted/60")}>{t($ => $.retry)}</button>
      )}
      {language.catalogStatus === "error" && (
        <button type="button" onClick={() => void language.retryCatalog()} className={cn(rowClass, "text-left hover:bg-muted/60")}>{t($ => $.retryTranslation)}</button>
      )}
    </div>
  );
};
